import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import ContestantHeader from '../../components/ContestantHeader';
import AudioControls from '../../components/AudioControls';
import './contestant.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function audioPath(url) {
  if (!url) return null;
  try { return new URL(url).pathname; } catch { return url; }
}

// The play + download buttons are the shared components/AudioControls, the
// same pair the admin and judge pages use (13 Sep 2026).

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function StatusBadge({ status }) {
  const map = {
    submitted:     'badge badge--submitted',
    approved:      'badge badge--approved',
    withdrawn:     'badge badge--withdrawn',
    disqualified:  'badge badge--disqualified',
  };
  return (
    <span className={map[status] ?? 'badge badge--withdrawn'}>
      {status ?? 'unknown'}
    </span>
  );
}

function PaymentBadge({ status }) {
  const map = {
    pending: 'badge badge--pending',
    paid:    'badge badge--paid',
    waived:  'badge badge--waived',
  };
  return (
    <span className={map[status] ?? 'badge badge--pending'}>
      {status ?? 'pending'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ContestantDashboard() {
  const [contestant, setContestant]         = useState(null);   // null = no profile
  const [songs, setSongs]                   = useState([]);
  const [settings, setSettings]             = useState(null);
  const [resultsPublished, setResultsPublished] = useState(false);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');

  // ── Audio playback ───────────────────────────────────────────────────────
  const [playingId, setPlayingId] = useState(null);

  // ── Inline delete state ──────────────────────────────────────────────────
  const [confirmDeleteId, setConfirmDeleteId]           = useState(null);
  const [deletingId, setDeletingId]                     = useState(null);
  const [deleteError, setDeleteError]                   = useState('');
  const [confirmDeleteDraftId, setConfirmDeleteDraftId] = useState(null);
  const [deletingDraftId, setDeletingDraftId]           = useState(null);

  // ── Past entries panel ───────────────────────────────────────────────────
  const [historyOpen,    setHistoryOpen]    = useState(false);
  const [history,        setHistory]        = useState(null); // null = not yet fetched
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError,   setHistoryError]   = useState('');

  useEffect(() => {
    Promise.all([
      // 404 means no contestant profile yet — treat as null, not an error
      api.get('/api/contestants/me').catch((e) => {
        if (e.response?.status === 404) return null;
        return Promise.reject(e);
      }),
      api.get('/api/contestants/settings'),
    ])
      .then(([cRes, sRes]) => {
        const c = cRes?.data?.contestant ?? null;
        setContestant(c);
        setSongs(c?.songs ?? []);
        setSettings(sRes.data.settings);
        setResultsPublished(cRes?.data?.results_published ?? false);
      })
      .catch(() => setError('Failed to load dashboard. Please try refreshing.'))
      .finally(() => setLoading(false));
  }, []);

  // ── Inline delete handler ────────────────────────────────────────────────
  const handleInlineDelete = async (songId) => {
    setDeletingId(songId);
    setDeleteError('');
    try {
      await api.delete(`/api/songs/${songId}`);
      // Remove the song from local state — the server confirmed deletion
      setSongs((prev) => prev.filter((s) => String(s.id) !== String(songId)));
      setDeletingId(null);
      setConfirmDeleteId(null);
    } catch (err) {
      setDeleteError(
        err.response?.data?.error ?? 'Failed to delete song. Please try again.'
      );
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  // ── Draft delete handler ─────────────────────────────────────────────────
  const handleDeleteDraft = async (draftId) => {
    setDeletingDraftId(draftId);
    try {
      await api.delete(`/api/songs/${draftId}`);
      setSongs((prev) => prev.filter((s) => String(s.id) !== String(draftId)));
      setDeletingDraftId(null);
      setConfirmDeleteDraftId(null);
    } catch (err) {
      setDeleteError(
        err.response?.data?.error ?? 'Failed to delete draft. Please try again.'
      );
      setDeletingDraftId(null);
      setConfirmDeleteDraftId(null);
    }
  };

  // ── Past entries toggle ──────────────────────────────────────────────────
  function toggleHistory() {
    const willOpen = !historyOpen;
    setHistoryOpen(willOpen);
    // Lazy-load: only fetch the first time the panel is opened
    if (willOpen && history === null && !historyLoading) {
      setHistoryLoading(true);
      setHistoryError('');
      api.get('/api/contestants/history')
        .then((res) => setHistory(res.data.history))
        .catch(() => setHistoryError('Failed to load history. Please try again.'))
        .finally(() => setHistoryLoading(false));
    }
  }

  function toggleAudio(songId, shouldPlay) {
    if (playingId && playingId !== songId) {
      const prev = document.getElementById(`audio-${playingId}`);
      if (prev) { prev.pause(); prev.currentTime = 0; }
    }
    const audio = document.getElementById(`audio-${songId}`);
    if (!audio) return;
    if (shouldPlay) { audio.play(); setPlayingId(songId); }
    else            { audio.pause(); audio.currentTime = 0; setPlayingId(null); }
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" aria-label="Loading…" />
            <p>Loading your dashboard…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-alert" role="alert">{error}</div>
        </main>
      </div>
    );
  }

  // ── Derived values ───────────────────────────────────────────────────────
  const draftSongs   = songs.filter((s) => s.status === 'draft');
  const activeSongs  = songs.filter((s) => s.status !== 'withdrawn' && s.status !== 'draft');
  const usedSlots    = activeSongs.length;
  const maxSlots     = settings?.max_songs_per_contestant ?? 3;
  const regOpen      = settings?.registration_open ?? false;
  const canSubmit    = regOpen && usedSlots < maxSlots;

  // ── No profile yet ───────────────────────────────────────────────────────
  if (!contestant) {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-section-title">Contestant Dashboard</div>

          {!regOpen && (
            <div className="c-alert c-alert--info" role="status">
              Registration is currently closed. Check back later.
            </div>
          )}

          <div className="c-empty">
            <div className="c-empty__icon">🎵</div>
            <h3 className="c-empty__title">You haven't submitted any songs yet</h3>
            <p className="c-empty__text">
              Complete your contestant profile and submit your first song to get started.
            </p>
            {regOpen ? (
              <Link to="/contestant/submit" className="c-btn">
                Submit your first song →
              </Link>
            ) : (
              <span className="c-btn c-btn--disabled" aria-disabled="true">
                Submissions are closed
              </span>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ── Normal dashboard ─────────────────────────────────────────────────────
  return (
    <div className="c-page">
      <ContestantHeader />

      <main className="c-main">

        {/* Welcome */}
        <div className="c-section-title">
          Welcome, {contestant.full_name || contestant.contact_name || 'Contestant'}!
        </div>

        {/* Cross-promotion: Performer registration */}
        <div className="c-alert c-alert--info" role="status">
          Would you like to perform a long or short set of your own music in front of a SCALA
          audience? Our audiences are unbelievably supportive and enthusiastic. If you would,
          consider registering as a SCALA performer.{' '}
          <a
            href="https://performer.scala.org.au"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 700 }}
          >
            Click here to register!
          </a>
        </div>

        {/* Competition status banners */}
        {settings?.competition_status === 'entries_paused' && (
          <div className="c-alert c-alert--warning" role="status">
            Song entries are currently closed.
          </div>
        )}
        {settings?.competition_status === 'all_paused' && (
          <div className="c-alert c-alert--warning" role="status">
            The competition is currently paused.
          </div>
        )}
        {settings?.competition_status === 'finished' && (
          <div className="c-alert c-alert--warning" role="status">
            This competition has been finalised.
          </div>
        )}

        {/* Registration-closed notice */}
        {!regOpen && (
          <div className="c-alert c-alert--info" role="status">
            Song submission is currently closed. Your existing submissions are shown below.
          </div>
        )}

        {/* Delete error banner */}
        {deleteError && (
          <div className="c-alert" role="alert" style={{ marginBottom: '1rem' }}>
            {deleteError}
          </div>
        )}

        {/* Draft songs section */}
        {draftSongs.length > 0 && (
          <div className="c-draft-section">
            <h2 className="c-draft-section__heading">Drafts — not yet submitted</h2>
            <div className="c-song-list">
              {draftSongs.map((draft) => (
                <div key={draft.id} className="c-song-card c-song-card--draft">
                  <div className="c-song-card__header">
                    <span className="c-song-card__title">
                      {draft.title || 'Untitled song'}
                    </span>
                    <span className="badge badge--draft">draft</span>
                  </div>
                  <div className="c-song-card__actions">
                    <div className="c-song-card__action-btns">
                      <Link
                        to={`/contestant/submit?draftId=${draft.id}`}
                        className="c-btn c-btn--outline c-btn--sm"
                      >
                        ✏ Continue editing
                      </Link>
                      {confirmDeleteDraftId === draft.id ? (
                        <div className="c-inline-confirm">
                          <span className="c-inline-confirm__text">Delete this draft?</span>
                          <button
                            type="button"
                            className="c-btn c-btn--danger c-btn--sm"
                            onClick={() => handleDeleteDraft(draft.id)}
                            disabled={deletingDraftId === draft.id}
                          >
                            {deletingDraftId === draft.id ? 'Deleting…' : 'Yes, delete'}
                          </button>
                          <button
                            type="button"
                            className="c-btn c-btn--outline c-btn--sm"
                            onClick={() => setConfirmDeleteDraftId(null)}
                            disabled={deletingDraftId === draft.id}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="c-btn c-btn--danger c-btn--sm"
                          onClick={() => { setConfirmDeleteDraftId(draft.id); setDeleteError(''); }}
                        >
                          🗑 Delete draft
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submission quota card */}
        <div className="c-quota">
          <div className="c-quota__info">
            <strong className="c-quota__count">
              {usedSlots} of {maxSlots}
            </strong>{' '}
            submission slot{maxSlots !== 1 ? 's' : ''} used
          </div>
          <div className="c-quota__action">
            {canSubmit ? (
              <Link to="/contestant/submit" className="c-btn">
                + Submit a Song
              </Link>
            ) : !regOpen ? (
              <span className="c-btn c-btn--disabled" aria-disabled="true">
                Submissions closed
              </span>
            ) : (
              <span className="c-btn c-btn--disabled" aria-disabled="true">
                Maximum reached
              </span>
            )}
          </div>
        </div>

        {/* Song list */}
        <h2 className="c-list-heading">Your Songs</h2>

        {/* Results status notice */}
        {songs.length > 0 && !resultsPublished && (
          <div className="c-alert c-alert--info" role="status" style={{ marginBottom: '1rem' }}>
            Results will be available once judging is complete.
          </div>
        )}

        {songs.length === 0 ? (
          <div className="c-empty">
            <div className="c-empty__icon">🎶</div>
            <h3 className="c-empty__title">No songs submitted yet</h3>
            {canSubmit && (
              <Link to="/contestant/submit" className="c-btn" style={{ marginTop: '1rem' }}>
                Submit your first song →
              </Link>
            )}
          </div>
        ) : (
          <div className="c-song-list">
            {songs.map((song) => (
              <div
                key={song.id}
                className={`c-song-card${song.payment_status === 'pending' ? ' c-song-card--pending-payment' : ''}`}
              >
                <div className="c-song-card__header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AudioControls
                      songId={song.id}
                      audioUrl={song.audio_url ? audioPath(song.audio_url) : null}
                      playingId={playingId}
                      onToggle={toggleAudio}
                    />
                    <span className="c-song-card__title">{song.title}</span>
                  </div>
                  <div className="c-song-card__badges">
                    {song.placement_category_name && (
                      <span className={
                        song.placement_category_name === 'Winner'    ? 'badge badge--winner'
                        : song.placement_category_name === 'Runner-up' ? 'badge badge--runner-up'
                        : 'badge badge--submitted'
                      }>
                        {song.placement_category_name === 'Winner'    ? '🥇 ' :
                         song.placement_category_name === 'Runner-up' ? '🥈 ' : '🏆 '}
                        {song.placement_category_name}
                      </span>
                    )}
                    <StatusBadge  status={song.status} />
                    <PaymentBadge status={song.payment_status} />
                  </div>
                </div>

                <div className="c-song-card__meta">
                  {song.artist && (
                    <span className="c-song-card__meta-item">
                      <span className="c-song-card__meta-label">Artist:</span>{' '}
                      {song.artist}
                    </span>
                  )}
                  <span className="c-song-card__meta-item">
                    <span className="c-song-card__meta-label">Category:</span>{' '}
                    {song.category_name}
                  </span>
                  <span className="c-song-card__meta-item">
                    <span className="c-song-card__meta-label">Submitted:</span>{' '}
                    {formatDate(song.created_at)}
                  </span>
                </div>

                {/* ── Song card actions ── */}
                <div className={`c-song-card__actions${song.payment_status === 'pending' ? ' c-song-card__actions--pending' : ''}`}>
                  {song.payment_status === 'pending' && (
                    <span className="c-pay-prompt">⚠ Entry fee outstanding</span>
                  )}

                  <div className="c-song-card__action-btns">
                    {/* Review / Edit — available for paid songs too. Paying
                        doesn't freeze an entry: contestants still need to fix a
                        typo or swap in a better mix. The song page itself
                        applies the real limits — delete is hidden once paid,
                        and the music file locks once a judge has scored it. */}
                    <Link
                      to={`/contestant/songs/${song.id}`}
                      className="c-btn c-btn--outline c-btn--sm"
                    >
                      Review / Edit →
                    </Link>

                    {/* Pay now — only for pending songs */}
                    {song.payment_status === 'pending' && (
                      <Link to="/contestant/payment" className="c-btn c-btn--pay-now c-btn--sm">
                        💳 Pay now →
                      </Link>
                    )}

                    {/* Inline Delete — only for pending songs */}
                    {song.payment_status === 'pending' && (
                      confirmDeleteId === song.id ? (
                        <div className="c-inline-confirm">
                          <span className="c-inline-confirm__text">Delete this song?</span>
                          <button
                            type="button"
                            className="c-btn c-btn--danger c-btn--sm"
                            onClick={() => handleInlineDelete(song.id)}
                            disabled={deletingId === song.id}
                          >
                            {deletingId === song.id ? 'Deleting…' : 'Yes, delete'}
                          </button>
                          <button
                            type="button"
                            className="c-btn c-btn--outline c-btn--sm"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={deletingId === song.id}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="c-btn c-btn--danger c-btn--sm"
                          onClick={() => { setConfirmDeleteId(song.id); setDeleteError(''); }}
                        >
                          🗑 Delete
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Results panel */}
                {resultsPublished && (
                  song.results_available ? (
                    <div className="c-results">
                      <div className="c-results__heading">Your Results</div>

                      {/* Overall score */}
                      {song.overall_avg != null && (
                        <div className="c-results__overall">
                          <span className="c-results__overall-label">Overall Score</span>
                          <span className="c-results__overall-value">
                            {song.overall_avg}
                            <span className="c-results__overall-max"> / {song.max_score}</span>
                          </span>
                        </div>
                      )}

                      {/* Per-criterion scores */}
                      {song.criterion_scores.length > 0 && (
                        <div className="c-criteria-list">
                          {song.criterion_scores.map((cs) => (
                            <div key={cs.criterion_name} className="c-criterion-row">
                              <span className="c-criterion-row__name">{cs.criterion_name}</span>
                              <div className="c-criterion-bar">
                                <div
                                  className="c-criterion-bar__fill"
                                  style={{ width: `${(cs.avg_score / cs.max_score) * 100}%` }}
                                />
                              </div>
                              <span className="c-criterion-row__score">{cs.display}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Anonymised judge comments */}
                      {song.comments.length > 0 && (
                        <div className="c-judge-comments">
                          <div className="c-judge-comments__heading">Judge Comments</div>
                          {song.comments.map((comment, i) => (
                            <blockquote key={i} className="c-judge-comment">
                              {comment}
                            </blockquote>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="c-results c-results--pending">
                      <span className="c-results__pending-text">
                        Judging in progress for this song — results pending.
                      </span>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}
        {/* ── Past entries collapsible panel ── */}
        {contestant && (
          <div className="c-history-panel">
            <button
              type="button"
              className="c-history-toggle"
              onClick={toggleHistory}
              aria-expanded={historyOpen}
            >
              <span className="c-history-toggle__text">See your past entries</span>
              <span className={`c-history-toggle__chevron${historyOpen ? ' c-history-toggle__chevron--open' : ''}`}>▼</span>
            </button>

            <div className={`c-history-body${historyOpen ? ' c-history-body--open' : ''}`}>
              <div className="c-history-body__inner">
                {historyLoading && (
                  <div className="c-loading" style={{ padding: '1.5rem 0' }}>
                    <div className="c-spinner" aria-label="Loading…" />
                    <p>Loading history…</p>
                  </div>
                )}

                {historyError && (
                  <div className="c-alert" role="alert">{historyError}</div>
                )}

                {!historyLoading && !historyError && history !== null && (
                  history.length === 0 ? (
                    <div className="c-history-empty">
                      <span className="c-history-empty__icon">🎵</span>
                      <p>Nothing here yet — your competition history will appear here after your first competition is complete.</p>
                    </div>
                  ) : (
                    history.map((comp) => (
                      <div key={comp.competition_id} className="c-history-competition">
                        <h3 className="c-history-year">{comp.year} Competition</h3>
                        <div className="c-history-songs">
                          {comp.songs.map((song) => (
                            <div key={song.id} className="c-history-song-card">
                              <div className="c-history-song-card__header">
                                {/* Past entries are playable and downloadable
                                    (13 Sep 2026): the files were always kept,
                                    the history view just never offered them.
                                    playerKey keeps this <audio> distinct from
                                    the current-songs list above. */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                                  <AudioControls
                                    songId={song.id}
                                    playerKey={`h${song.id}`}
                                    audioUrl={song.audio_url ? audioPath(song.audio_url) : null}
                                    playingId={playingId}
                                    onToggle={toggleAudio}
                                  />
                                  <span className="c-history-song-card__title">{song.title}</span>
                                </div>
                                <div className="c-history-song-card__badges">
                                  {song.placement_category_name && (
                                    <span className={
                                      song.placement_category_name === 'Winner'    ? 'badge badge--winner'
                                      : song.placement_category_name === 'Runner-up' ? 'badge badge--runner-up'
                                      : 'badge badge--submitted'
                                    }>
                                      {song.placement_category_name === 'Winner'    ? '🥇 ' :
                                       song.placement_category_name === 'Runner-up' ? '🥈 ' : '🏆 '}
                                      {song.placement_category_name}
                                    </span>
                                  )}
                                  {song.results_pending && !song.results_available && (
                                    <span className="badge badge--pending">Results pending</span>
                                  )}
                                </div>
                              </div>

                              <div className="c-history-song-card__meta">
                                <span>
                                  <span className="c-history-song-card__meta-label">Category:</span>{' '}
                                  {song.category_name}
                                </span>
                                {song.artist && (
                                  <span>
                                    <span className="c-history-song-card__meta-label">Artist:</span>{' '}
                                    {song.artist}
                                  </span>
                                )}
                                <span>
                                  <span className="c-history-song-card__meta-label">Submitted:</span>{' '}
                                  {formatDate(song.submitted_at)}
                                </span>
                              </div>

                              {song.results_available && (
                                <div className="c-results">
                                  <div className="c-results__heading">Results</div>

                                  {song.overall_avg != null && (
                                    <div className="c-results__overall">
                                      <span className="c-results__overall-label">Overall Score</span>
                                      <span className="c-results__overall-value">
                                        {song.overall_avg}
                                        <span className="c-results__overall-max"> / {song.max_score}</span>
                                      </span>
                                    </div>
                                  )}

                                  {song.criterion_scores.length > 0 && (
                                    <div className="c-criteria-list">
                                      {song.criterion_scores.map((cs) => (
                                        <div key={cs.criterion_name} className="c-criterion-row">
                                          <span className="c-criterion-row__name">{cs.criterion_name}</span>
                                          <div className="c-criterion-bar">
                                            <div
                                              className="c-criterion-bar__fill"
                                              style={{ width: `${(cs.avg_score / cs.max_score) * 100}%` }}
                                            />
                                          </div>
                                          <span className="c-criterion-row__score">{cs.display}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {song.comments.length > 0 && (
                                    <div className="c-judge-comments">
                                      <div className="c-judge-comments__heading">Judge feedback</div>
                                      {song.comments.map((comment, ci) => (
                                        <blockquote key={ci} className="c-judge-comment">
                                          {comment}
                                        </blockquote>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )
                )}
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
